import { Logger } from '@deqo/foundation';
import RBush, { BBox } from 'rbush';
import { Component, ComponentType, Convention, ObjectManager } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { ActiveObject, resolve } from '../../Container';
import { Physical, RelativeTo } from '../entities/Physical';

interface Entry {
    readonly minX: number;
    readonly minY: number;
    readonly maxX: number;
    readonly maxY: number;
    readonly object: Physical;
}

const Entry = (object: Physical) => {
    // If we have a 0 area rect, inflate the rect by 1 on the required axis to ensure it is indexed
    const initial = object.area(RelativeTo.Model);
    const { left, top, right, bottom } = initial.inflate([
        initial.width === 0 ? 1 : 0,
        initial.height === 0 ? 1 : 0,
    ]);
    return Object.freeze({ minX: left, minY: top, maxX: right, maxY: bottom, object });
};

const toBBox = (rop: Rect | Point): BBox => {
    if (rop instanceof Point) {
        return toBBox(new Rect(rop.x - .5, rop.y - .5, 1, 1));
    }
    return { minX: rop.left, minY: rop.top, maxX: rop.right, maxY: rop.bottom };
};

const logErrors = Logger.get('app.errors');
const logMetrics = Logger.get('app.metrics');

export class SpatialIndex extends ActiveObject {

    @resolve(ObjectManager) private objects!: ObjectManager;

    private readonly index = new RBush<Entry>();
    private readonly register = new Map<string, Entry>();
    private readonly invalidations = new Set<string>();

    public start(): void {
        this.rebuild();
        this.burden(
            this.objects.createTrigger('SpatialIndex.invalidate', { priority: true })
                .forAllTypes()
                .includingDead()
                .onCreated().onDeleted()
                .onChangesTo('x', 'y', 'width', 'height', 'rotation')
                .doForEach(this.doInvalidateComponent),
            this.objects.on('component', 'allocated', this.doInvalidateComponent),
            this.objects.on('component', 'affected', this.doInvalidateComponent),
            this.objects.on('component', 'released', this.doInvalidateComponent),
        );
    }

    public invalidate(id: string);
    public invalidate(pc: Physical);
    public invalidate(idOrComponent: string | Physical) {
        const id = typeof idOrComponent === 'string' ? idOrComponent : idOrComponent.id;
        this.invalidations.add(id);
    }

    public lookup<T extends Physical = Physical>(rectOrPt: Rect | Point, typeOrConvention?: ComponentType<T> | Convention<T>): Set<T> {
        this.refresh();
        const results = new Set<T>();
        const entries = this.index.search(toBBox(rectOrPt));
        for (const { object } of entries) {
            if (!object.alive || !object.mounted()) {
                logErrors.warn('Dead element in spatial index:', object.type, object.id);
                this.invalidations.add(object.id);
            }
            else {
                if (!typeOrConvention || object.is(typeOrConvention)) {
                    results.add(object as T);
                }
            }
        }
        return results;
    }

    public test<T extends Physical = Physical>(mpt: Point, ct?: ComponentType<T>): Set<T>;
    public test<T extends Physical = Physical>(mpt: Point, cv?: Convention<T>): Set<T>;
    public test(mpt: Point, toc: any) {
        const results = new Set<any>();
        for (const pc of this.lookup(mpt)) {
            if ((!toc || pc.is(toc)) && pc.test(mpt)) {
                results.add(pc);
            }
        }
        return results;
    }

    private rebuild() {
        const { objects } = this;
        const t = performance.now();

        this.register.clear();
        this.index.clear();

        const all = Array.from(objects.getAll(Physical)).filter(x => !!x.parentPhysical);
        const entries = all.map(x => this.register.set(x.id, Entry(x)).get(x.id));
        this.index.load(entries);

        logMetrics.log('SpatialIndex.rebuild', (performance.now() - t) + 'ms');
    }

    private refresh() {
        if (!this.invalidations.size) return;
        const t = performance.now();

        const active = new Set<Physical>();
        const expired = new Set<string>();

        // Remove any inactive, and expand then add those that are
        for (const id of this.invalidations) {
            const p = this.objects.getMaybe(id, Physical);
            if (p && p.alive && !!p.parent && p.mounted()) {
                active.add(p);
                p.visitAll(Physical, c => void (active.add(c)));
            }
            else {
                expired.add(id);
            }
        }

        for (const id of expired) {
            this.remove(id);
        }
        for (const p of active) {
            this.addOrUpdate(p);
        }

        this.invalidations.clear();
        logMetrics.log('SpatialIndex.refresh', (performance.now() - t) + 'ms');
    }

    private addOrUpdate(p: Physical) {
        this.remove(p.id);
        const e = this.register.set(p.id, Entry(p)).get(p.id);
        this.index.insert(e);
    }

    private remove(id: string) {
        const e = this.register.get(id);
        if (e) {
            this.register.delete(id);
            this.index.remove(e);
        }
    }

    private doInvalidateComponent = (input: Component | [Component]) => {
        const pc = (Array.isArray(input) ? input[0] : input).as(Physical);
        if (pc) {
            this.invalidate(pc);
        }
    };
}
